/**
 * Subscription-related TypeScript interfaces and types.
 *
 * Defines the shape of subscription data for type safety throughout the frontend.
 */

export interface PlanFeatures {
  simulationsPerMonth: number;
  allowedUseCases: string[] | 'all';
  maxPersonasPerSimulation: number;
  maxSegments: number;
  // Optional: no backend plan defines max_audiences and no surface emits it, so
  // declaring it required was a type that the wire never satisfies.
  maxAudiences?: number;
  prioritySupport: boolean;
  apiAccess?: boolean;
  customBranding?: boolean;
  dedicatedAccountManager?: boolean;
}

export interface SubscriptionPlan {
  id: string;
  name: string;
  price: number;
  description?: string;
  isPopular?: boolean;
  features: PlanFeatures;
}

export interface UsageInfo {
  // Monthly refreshable counters
  simulationsUsed: number;
  simulationsLimit: number;
  questionsUsed: number; // Total questions parsed this month
  groupChatsUsed: number;
  groupChatsLimit: number;
  simulationChatsUsed: number;
  simulationChatsLimit: number;
  /** Per-simulation cap; absent on responses from before it existed. */
  simulationChatsPerSimulationLimit?: number;

  // Static validation limits (not counters)
  questionsPerSurvey: number; // e.g., 4 - max questions per individual survey
  optionsPerQuestion: number; // e.g., 5 - max options per individual question

  // Legacy fields for compatibility
  usagePercentage: number;
  remainingSimulations: number;
  daysUntilReset: number;
  useCaseBreakdown: Record<string, number>;
  periodStart: string;
  periodEnd: string;
}

export interface CurrentSubscription {
  id: string;
  name: string;
  price: number;
  status: 'active' | 'cancelled' | 'past_due' | 'trialing' | 'expired' | 'pending';
  startDate?: string;
  endDate?: string;
  cancelAtPeriodEnd: boolean;
  usage: {
    simulationsUsed: number;
    simulationsLimit: number;
    resetDate?: string;
  };
}

export interface FeatureAccess {
  currentPlan: string;
  planFeatures: {
    simulationsPerMonth: number;
    maxPersonasPerSimulation: number;
    maxSegments: number;
    // Optional: no backend surface emits max_audiences (see PlanFeatures).
    maxAudiences?: number;
    prioritySupport: boolean;
    apiAccess: boolean;
    customBranding: boolean;
  };
  useCases: Record<
    string,
    {
      displayName: string;
      description: string;
      category: string;
      hasAccess: boolean;
      minPlanRequired: string;
    }
  >;
  isAdmin: boolean;
}

export interface CheckoutSession {
  checkoutUrl: string;
  sessionId: string;
}

export interface AccessCheck {
  hasAccess: boolean;
  reason: string;
  currentPlan?: string;
  requiredPlan?: string;
  allowedUseCases?: string[];
}

export interface BillingPortalSession {
  portalUrl: string;
}

export interface SubscriptionError {
  success: false;
  error: string;
  details?: any;
}

export interface SubscriptionSuccess<T = any> {
  success: true;
  data?: T;
  message?: string;
}

export type SubscriptionResponse<T = any> = SubscriptionSuccess<T> | SubscriptionError;

// Plan tier constants
export const PLAN_TIERS = {
  FREE: 'free',
  STARTER: 'starter',
  PROFESSIONAL: 'professional',
  BUSINESS: 'business',
  ENTERPRISE: 'enterprise',
} as const;

export type PlanTier = (typeof PLAN_TIERS)[keyof typeof PLAN_TIERS];

// Subscription status constants
export const SUBSCRIPTION_STATUS = {
  ACTIVE: 'active',
  CANCELLED: 'cancelled',
  PAST_DUE: 'past_due',
  TRIALING: 'trialing',
  EXPIRED: 'expired',
  PENDING: 'pending',
} as const;

export type SubscriptionStatus = (typeof SUBSCRIPTION_STATUS)[keyof typeof SUBSCRIPTION_STATUS];

// Use case categories
export const USE_CASE_CATEGORIES = {
  RESEARCH: 'research',
  TESTING: 'testing',
  CREATIVE: 'creative',
  STRATEGY: 'strategy',
  MARKETPLACE: 'marketplace',
  ADMIN: 'admin',
} as const;

export type UseCaseCategory = (typeof USE_CASE_CATEGORIES)[keyof typeof USE_CASE_CATEGORIES];

/** A population the caller may run a concept test against, named as THIS client names it. */
export interface ClientPopulationOption {
  name: string;
  displayName: string;
}

/** One entry in a client's chat picker. The label is the CLIENT's, not the registry's, which
 *  carries internal detail ("Jerky & Meat-Snack Consumers (All — 305)"). */
export interface ClientChatSegment {
  id: number;
  label: string;
}

/** The caller's chat-surface picker, with ids already resolved for this environment. */
export interface ClientChatPicker {
  defaultSegmentId: number;
  dropdownVisible: boolean;
  segments: ClientChatSegment[];
}

/**
 * The caller's own screen configuration, served by `/plans/current`.
 *
 * Replaces matching the signed-in address against the bundled `clients.json`: the server knows
 * who is asking, so a new client is a database row rather than a rebuild. Null while entitlements
 * are still loading, and from a backend deployed behind this build.
 */
/**
 * Screen settings for one surface, as {key: value}. Deliberately untyped per key: the server
 * decides what a surface carries, so adding one is a database row and a read at the call site
 * rather than a change here.
 */
export type ClientWiring = Record<string, Record<string, unknown>>;

export interface ClientConfig {
  /** The account's own ticked use cases, or null when nothing narrows them. Read from the caller's
   *  organization rather than the plan, so it is correct in `legacy` mode too. */
  useCases: string[] | null;
  populations: ClientPopulationOption[];
  chatPicker: ClientChatPicker | null;
  wiring: ClientWiring;
}

/** One wiring value, or `fallback` when this account has no configuration for it. */
export const wiringValue = <T>(
  config: ClientConfig | null,
  surface: string,
  key: string,
  fallback: T,
): T => {
  const value = config?.wiring?.[surface]?.[key];
  return value === undefined || value === null ? fallback : (value as T);
};

/** A survey-chat card this account may open. */
export interface SurveyChatTarget {
  surveyId: number;
  groupId: number | null;
  label: string;
}
